/**
 * Hook for fetching workflow metadata from the dedicated metadata API.
 * 
 * This operates parallel to workflow streams, not through them.
 * Metadata is fetched on-demand and polled for updates when workflows are active.
 */
import { useEffect, useRef, useCallback } from 'react';
import { useWorkflowMetadata } from '../contexts/WorkflowMetadataContext';

interface UseWorkflowMetadataFetchOptions {
  /** Whether to enable metadata fetching */
  enabled?: boolean;
  /** Polling interval in milliseconds (default: 3000) */
  pollingInterval?: number;
  /** Whether to poll for updates (default: true when enabled) */
  enablePolling?: boolean;
}

export const useWorkflowMetadataFetch = (
  contextId: string | undefined,
  options: UseWorkflowMetadataFetchOptions = {}
) => {
  const {
    enabled = true,
    pollingInterval = 3000,
    enablePolling = true
  } = options;

  const { updateMetadata, clearMetadata } = useWorkflowMetadata();
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const isMountedRef = useRef(true);

  const fetchMetadata = useCallback(async () => {
    if (!contextId || !enabled || !isMountedRef.current) return;

    try {
      const token = localStorage.getItem('access_token');
      const headers: HeadersInit = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await fetch(`/api/workflow/metadata/${contextId}`, {
        headers
      });
      
      if (!response.ok) {
        // If 404, context doesn't exist yet or has no metadata
        if (response.status === 404) {
          console.debug(`No metadata available for context ${contextId}`);
          return;
        }
        throw new Error(`Failed to fetch metadata: ${response.statusText}`);
      }

      const data = await response.json();
      
      // Only update if component is still mounted
      if (!isMountedRef.current) return;

      // Update each metadata category if present
      if (data.session) {
        updateMetadata('session', data.session);
      }
      
      if (data.workflow) {
        updateMetadata('workflow', data.workflow);
      }
      
      if (data.taskIdentification) {
        updateMetadata('taskIdentification', data.taskIdentification);
      }
      
      if (data.knowledgeBase) {
        updateMetadata('knowledgeBase', data.knowledgeBase);
      }
      
      if (data.performance) {
        updateMetadata('performance', data.performance);
      }
      
      if (data.audit) {
        updateMetadata('audit', data.audit);
      }

      // Additional metadata categories
      if (data.scriptExecution) {
        updateMetadata('scriptExecution' as any, data.scriptExecution);
      }

      if (data.columnMappings) {
        updateMetadata('columnMappings' as any, data.columnMappings);
      }

    } catch (error) {
      console.error('Error fetching workflow metadata:', error);
      // Don't throw - metadata fetch failures shouldn't break the UI
    }
  }, [contextId, enabled, updateMetadata]);

  // Set up polling
  useEffect(() => {
    isMountedRef.current = true;

    if (!contextId || !enabled) {
      return;
    }

    // Initial fetch
    fetchMetadata();

    // Set up polling if enabled
    if (enablePolling && pollingInterval > 0) {
      intervalRef.current = setInterval(fetchMetadata, pollingInterval);
    }

    // Cleanup
    return () => {
      isMountedRef.current = false;
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [contextId, enabled, enablePolling, pollingInterval, fetchMetadata]);

  // Clear metadata when context changes
  useEffect(() => {
    if (!contextId) {
      clearMetadata();
    }
  }, [contextId, clearMetadata]);

  return {
    fetchMetadata,
    refetch: fetchMetadata
  };
};